import { useEffect } from 'react';
import { mapToId } from '@witmetrics/utils/arrays';
import { useActivePractice, useDispatch } from '@witmetrics/data-store';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import Overlay from '@/components/Overlay';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import TabsColumn from './TabsColumn';
import PatientHeader from './PatientHeader';
import TimelineSection from './TimelineSection';
import {
  classes,
  fetchFilesData,
  fetchPatientProjects,
  fetchPracticeData,
  fetchProjectsData,
} from './utils';

export default function PatientOverlay() {
  const dispatch = useDispatch();
  const { onApiError, activeLeadId, onClearActiveLead } = useAppState();
  const activePractice = useActivePractice();
  const [isFetchingData, toggleFetchingData] = useToggle(true);
  const [isFetchingFiles, toggleFetchingFiles] = useToggle(true);

  useEffect(() => {
    if (activeLeadId && activePractice) fetchData();
  }, [activeLeadId, activePractice]);

  const fetchData = async () => {
    try {
      toggleFetchingData(true);
      const [projects, practiceData] = await Promise.all([
        fetchPatientProjects(activeLeadId!),
        fetchPracticeData(activePractice!.id),
      ]);
      dispatch.addProjects(projects);
      dispatch.addPracticeUsers(practiceData.practiceUsers);
      dispatch.addKeypointTypes(practiceData.keypointTypes);
      const projectIds = mapToId(projects);
      fetchFiles(projectIds); // No need to await the files
      const projectsData = await fetchProjectsData(projectIds);
      dispatch.addProjectTasks(projectsData.tasks);
      dispatch.addProjectNotes(projectsData.notes);
      dispatch.addProjectKeypoints(projectsData.keypoints);
      dispatch.addProjectOrders(projectsData.orders);
      dispatch.addProjectScanServices(projectsData.services);
      toggleFetchingData(false);
    } catch (err) {
      onApiError(err, 'Error fetching patient data', () => fetchData());
    }
  };

  const fetchFiles = async (projectIds: number[]) => {
    try {
      toggleFetchingFiles(true);
      const results = await fetchFilesData(projectIds);
      dispatch.addProjectFileCollections(results.projectFileCollections);
      dispatch.addFileCollectionFiles(results.fileCollectionFiles);
      dispatch.addFileNotes(results.fileNotes);
      dispatch.addFileCollections(results.fileCollections);
      toggleFetchingFiles(false);
    } catch (err) {
      onApiError(err, 'Error fetching files', () => fetchFiles(projectIds));
    }
  };

  if (!activeLeadId) return null;

  return (
    <Overlay className={classes.overlay} onClose={onClearActiveLead}>
      <PatientHeader />
      <div className={classes.content}>
        <div className={classes.leftColumn}>
          {isFetchingData ? (
            <div className={classes.loading}>
              Fetching patient
              <LoopingEllipsis />
            </div>
          ) : (
            <TimelineSection />
          )}
        </div>
        <TabsColumn isFetchingData={isFetchingData} />
      </div>
    </Overlay>
  );
}
